import React from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GameroomEvent } from '@/redux/types/room'
import { getAutoReleaseDropdownLabel } from './autoReleaseUtils'
import { RoundConfig, TimerMode } from './types'
import { CharterRoundConfig } from './CharterRoundConfig'

interface SingleRoundConfigProps {
  roundConfig: RoundConfig
  mode: TimerMode
  events: GameroomEvent[]
  onUpdateConfig: (
    key: keyof RoundConfig,
    value: number | boolean | number[]
  ) => void
  disabled?: boolean
  // Round 0 (Pre-Game Charter) — rendered above the phase durations when enabled.
  charterEnabled?: boolean
  charterGate?: boolean
  onCharterChange?: (patch: { charter_gate_enabled?: boolean }) => void
}

export const SingleRoundConfig: React.FC<SingleRoundConfigProps> = ({
  roundConfig,
  mode,
  events,
  onUpdateConfig,
  disabled = false,
  charterEnabled = false,
  charterGate = false,
  onCharterChange,
}) => {
  const charterSection =
    onCharterChange && charterEnabled ? (
      <div className='rounded-md border p-4'>
        <CharterRoundConfig
          gate={charterGate}
          onChange={onCharterChange}
          disabled={disabled}
        />
      </div>
    ) : null

  const selectedAutoReleaseEventIds = roundConfig?.autoReleaseEventIds ?? []

  const isAutoReleaseEnabled = roundConfig?.autoReleaseEnabled ?? false

  const autoReleaseDropdownLabel = getAutoReleaseDropdownLabel(
    selectedAutoReleaseEventIds,
    events
  )

  if (mode === TimerMode.SINGLE_ROUND_ADVISOR_ONLY) {
    return (
      <div className='space-y-6'>
        {charterSection}
        <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
          <Label>Event Release on Round Start</Label>

          <div className='flex items-center space-x-2'>

            <Switch
              id='autoReleaseEnabled-single'
              checked={isAutoReleaseEnabled}
              onCheckedChange={(checked) =>
                onUpdateConfig('autoReleaseEnabled', checked)
              }
              disabled={disabled}
            />
            <Label
              htmlFor='autoReleaseEnabled-single'
              className='text-sm text-gray-600'
            >
                Automatic
            </Label>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={disabled || !isAutoReleaseEnabled}
                    className='w-full justify-between bg-white px-3 h-9 text-sm'
              >
                <span className='truncate text-sm text-gray-600'>
                  {autoReleaseDropdownLabel}
                </span>
                <ChevronDown className='h-4 w-4 opacity-60' />
              </Button>
            </DropdownMenuTrigger>
                <DropdownMenuContent className='max-h-56 overflow-y-auto w-[200px]'>
              {events.length === 0 ? (
                <div className='px-3 py-2 text-sm text-gray-500'>
                  No events available
                </div>
              ) : (
                events.map((event) => {
                  const isChecked =
                    selectedAutoReleaseEventIds.includes(event.id)
                  return (
                    <DropdownMenuCheckboxItem
                      key={event.id}
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        const nextIds = checked
                          ? Array.from(
                              new Set([
                                ...selectedAutoReleaseEventIds,
                                event.id,
                              ])
                            )
                          : selectedAutoReleaseEventIds.filter(
                              (id) => id !== event.id
                            )

                        onUpdateConfig('autoReleaseEventIds', nextIds)
                      }}
                      className='capitalize'
                    >
                      {event.title}
                    </DropdownMenuCheckboxItem>
                  )
                })
              )}
            </DropdownMenuContent>
          </DropdownMenu>

        </div>

        <h3 className='text-lg font-medium'>Phase Durations</h3>
        <div className='space-y-4'>
          <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
            <Label htmlFor='advisorConsultation'>Advisor Consultation</Label>
            <div className='flex items-center space-x-2'>
              <Switch
                id='advisorInfinite'
                checked={roundConfig.isAdvisorInfinite || false}
                onCheckedChange={(checked) =>
                  onUpdateConfig('isAdvisorInfinite', checked)
                }
                disabled={disabled}
              />
              <Label
                htmlFor='advisorInfinite'
                className='text-sm text-gray-600'
              >
                Infinite
              </Label>
            </div>
            <Input
              id='advisorConsultation'
              type='number'
              value={roundConfig.advisorConsultation}
              onChange={(e) =>
                onUpdateConfig('advisorConsultation', parseInt(e.target.value))
              }
              min={1}
              disabled={disabled || roundConfig.isAdvisorInfinite}
              className={roundConfig.isAdvisorInfinite ? 'opacity-50' : ''}
            />
            <span className='text-gray-500'>
              {roundConfig.isAdvisorInfinite ? '∞' : 'minutes'}
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {charterSection}
      <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
        <Label>Event Release on Round Start</Label>

        <div className='flex items-center space-x-2'>
          <Switch
            id='autoReleaseEnabled-single'
            checked={isAutoReleaseEnabled}
            onCheckedChange={(checked) =>
              onUpdateConfig('autoReleaseEnabled', checked)
            }
            disabled={disabled}
          />
          <Label
            htmlFor='autoReleaseEnabled-single'
            className='text-sm text-gray-600'
          >
            Automatic
          </Label>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={disabled || !isAutoReleaseEnabled}
              className='w-full justify-between bg-white px-3 h-9 text-sm'
            >
              <span className='truncate text-sm text-gray-600'>
                {autoReleaseDropdownLabel}
              </span>
              <ChevronDown className='h-4 w-4 opacity-60' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='max-h-56 overflow-y-auto w-[200px]'>
            {events.length === 0 ? (
              <div className='px-3 py-2 text-sm text-gray-500'>
                No events available
              </div>
            ) : (
              events.map((event) => {
                const isChecked =
                  selectedAutoReleaseEventIds.includes(event.id)
                return (
                  <DropdownMenuCheckboxItem
                    key={event.id}
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      const nextIds = checked
                        ? Array.from(
                            new Set([
                              ...selectedAutoReleaseEventIds,
                              event.id,
                            ])
                          )
                        : selectedAutoReleaseEventIds.filter(
                            (id) => id !== event.id
                          )

                      onUpdateConfig('autoReleaseEventIds', nextIds)
                    }}
                    className='capitalize'
                  >
                    {event.title}
                  </DropdownMenuCheckboxItem>
                )
              })
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <h3 className='text-lg font-medium'>Phase Durations</h3>
      <div className='space-y-4'>
        {roundConfig.isRandR && (
          <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
            <Label htmlFor='randrConsultation'>
              Event Analyst Consultation
            </Label>
            <div className='flex items-center space-x-2'>
              <Switch
                id='randrInfinite'
                checked={roundConfig.isRandrInfinite || false}
                onCheckedChange={(checked) =>
                  onUpdateConfig('isRandrInfinite', checked)
                }
                disabled={disabled}
              />
              <Label htmlFor='randrInfinite' className='text-sm text-gray-600'>
                Infinite
              </Label>
            </div>
            <Input
              id='randrConsultation'
              type='number'
              value={roundConfig.randrConsultation || 0}
              onChange={(e) =>
                onUpdateConfig('randrConsultation', parseInt(e.target.value))
              }
              min={1}
              disabled={disabled || roundConfig.isRandrInfinite}
              className={roundConfig.isRandrInfinite ? 'opacity-50' : ''}
            />
            <span className='text-gray-500'>
              {roundConfig.isRandrInfinite ? '∞' : 'minutes'}
            </span>
          </div>
        )}

        <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
          <Label htmlFor='advisorConsultation'>Advisor Consultation</Label>
          <div className='flex items-center space-x-2'>
            <Switch
              id='advisorInfinite'
              checked={roundConfig.isAdvisorInfinite || false}
              onCheckedChange={(checked) =>
                onUpdateConfig('isAdvisorInfinite', checked)
              }
              disabled={disabled}
            />
            <Label htmlFor='advisorInfinite' className='text-sm text-gray-600'>
              Infinite
            </Label>
          </div>
          <Input
            id='advisorConsultation'
            type='number'
            value={roundConfig.advisorConsultation}
            onChange={(e) =>
              onUpdateConfig('advisorConsultation', parseInt(e.target.value))
            }
            min={1}
            disabled={disabled || roundConfig.isAdvisorInfinite}
            className={roundConfig.isAdvisorInfinite ? 'opacity-50' : ''}
          />
          <span className='text-gray-500'>
            {roundConfig.isAdvisorInfinite ? '∞' : 'minutes'}
          </span>
        </div>

        <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
          <Label htmlFor='negotiation'>Negotiation</Label>
          <div className='flex items-center space-x-2'>
            <Switch
              id='negotiationInfinite'
              checked={roundConfig.isNegotiationInfinite || false}
              onCheckedChange={(checked) =>
                onUpdateConfig('isNegotiationInfinite', checked)
              }
              disabled={disabled}
            />
            <Label
              htmlFor='negotiationInfinite'
              className='text-sm text-gray-600'
            >
              Infinite
            </Label>
          </div>
          <Input
            id='negotiation'
            type='number'
            value={roundConfig.negotiation}
            onChange={(e) =>
              onUpdateConfig('negotiation', parseInt(e.target.value))
            }
            min={1}
            disabled={disabled || roundConfig.isNegotiationInfinite}
            className={roundConfig.isNegotiationInfinite ? 'opacity-50' : ''}
          />
          <span className='text-gray-500'>
            {roundConfig.isNegotiationInfinite ? '∞' : 'minutes'}
          </span>
        </div>

        <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
          <Label htmlFor='decisionSubmission'>Decision Submission</Label>
          <div className='flex items-center space-x-2'>
            <Switch
              id='decisionInfinite'
              checked={roundConfig.isDecisionInfinite || false}
              onCheckedChange={(checked) =>
                onUpdateConfig('isDecisionInfinite', checked)
              }
              disabled={disabled}
            />
            <Label htmlFor='decisionInfinite' className='text-sm text-gray-600'>
              Infinite
            </Label>
          </div>
          <Input
            id='decisionSubmission'
            type='number'
            value={roundConfig.decisionSubmission}
            onChange={(e) =>
              onUpdateConfig('decisionSubmission', parseInt(e.target.value))
            }
            min={1}
            disabled={disabled || roundConfig.isDecisionInfinite}
            className={roundConfig.isDecisionInfinite ? 'opacity-50' : ''}
          />
          <span className='text-gray-500'>
            {roundConfig.isDecisionInfinite ? '∞' : 'minutes'}
          </span>
        </div>
      </div>
    </div>
  )
}
